// Session-scoped stale-while-revalidate cache for the talent's tabs.
//
// Problem (founder decision): every tab visit remounted the page with
// empty state, flashing "Loading..." while data refetched — even though the
// DJ looked at the exact same numbers seconds ago.
//
// Pattern: pages SEED their state from the last good payload here (render
// instantly), then let their normal fetch run and overwrite silently. Plain
// in-memory Map on purpose — dies with the tab, nothing persisted, no
// invalidation protocol needed.
//
// ONE ACCOUNT AT A TIME (1.5.2): the store belongs to the signed-in account
// (AuthContext calls setOwner on every sign-in / sign-out). A different
// account — or nobody — wipes it first, so the next talent on a shared phone
// (SPA sign-out → sign-in never reloads the page) never sees the previous
// one's name, price or requests. Keys also carry the account (`part:userId`
// via hooks/useAccountCache, or the page's slug) as a second lock.
//
// DISPLAY ONLY: what comes out of here paints the screen. A money action
// (cash-out, Withdraw, the money gate, adding a card, accepting / playing /
// declining a request) acts on what the server says THIS visit — see the
// "fresh" values next to every cached one.
const store = new Map<string, unknown>();
let owner: string | null = null;

export const pageCache = {
  get<T>(key: string): T | undefined {
    return store.get(key) as T | undefined;
  },
  has(key: string): boolean {
    return store.has(key);
  },
  set(key: string, value: unknown): void {
    store.set(key, value);
  },
  /** The account the cached payloads belong to; anyone else (or none) clears the store. */
  setOwner(id: string | null | undefined): void {
    const next = id ? String(id) : null;
    if (next === owner) return;
    store.clear();
    owner = next;
  },
  /** The signed-in account's id, or null when nobody is signed in. */
  owner(): string | null {
    return owner;
  },
  clear(): void {
    store.clear();
  },
};
